import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Settings, Network, Users, CheckSquare, ListTodo, Megaphone, NotebookPen, FileSignature, Wallet, IdCard, LifeBuoy, CalendarClock, Contact, KeyRound, FileCheck2, Bot, Zap, PanelsTopLeft, PanelLeftClose, PanelLeftOpen, Menu, Landmark, ChevronDown, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useModuleAccess } from "@/context/PermissionsContext";
import { useAuth } from "@/context/AuthContext";
import { usePendingChanges } from "@/lib/changeRequests";
import { api } from "@/lib/api";

const buildNav = (isMaster, isSuper, agentAlerts = 0) => [
  { to: "/", label: "Dashboard Campagne", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti" },
  { to: "/calendar", label: "Calendario", icon: CalendarClock, testid: "nav-calendar" },
  { to: "/appunti", label: "Documenti", icon: NotebookPen, testid: "nav-appunti" },
  {
    label: "Attività", icon: CheckSquare, testid: "nav-group-tasks", group: true,
    children: [
      { to: "/tasks/operations", label: "Operazioni Mensili", icon: ListTodo, testid: "nav-tasks-operations" },
      { to: "/lead-router", label: "Lead Router", icon: Network, testid: "nav-lead-router" },
      { to: "/landing", label: "Landing", icon: PanelsTopLeft, testid: "nav-landing" },
      { to: "/tasks/support-tickets", label: isMaster ? "Ticket di supporto" : "Task e Ticket di supporto", icon: LifeBuoy, testid: "nav-tasks-support" },
    ],
  },
  {
    label: "Amministrazione", icon: Landmark, testid: "nav-group-admin", group: true,
    children: [
      { to: "/amministrazione", label: "Preventivi e Contratti", icon: FileSignature, testid: "nav-amministrazione", moduleKey: "amministrazione" },
      { to: "/contabilita", label: "Contabilità", icon: Wallet, testid: "nav-contabilita", moduleKey: "contabilita" },
      { to: "/crm", label: "CRM", icon: Contact, testid: "nav-crm" },
      { to: "/automazioni", label: "Automazioni", icon: Zap, testid: "nav-automazioni" },
      { to: "/hr", label: "Risorse Umane", icon: IdCard, testid: "nav-hr", moduleKey: "hr" },
      { to: "/consensi", label: "Consensi Foto/Video", icon: FileCheck2, testid: "nav-consensi" },
      { to: "/password", label: "Password", icon: KeyRound, testid: "nav-password" },
      ...(isSuper ? [{ to: "/accessi-agenti", label: agentAlerts ? `Accessi agenti (${agentAlerts})` : "Accessi agenti", icon: Bot, testid: "nav-agent-access" }] : []),
    ],
  },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni" },
];

const linkClasses = (active) =>
  cn(
    "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
    active ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-foreground"
  );

function SidebarBrand() {
  return (
    <div className="flex h-16 items-center gap-2.5 px-6">
      <img src="/logo-ae.png" alt="Aesthetic OS" className="h-7 w-auto object-contain" data-testid="sidebar-brand-logo" />
      <div className="leading-tight">
        <div className="font-display text-sm font-semibold tracking-tight text-foreground">
          Aesthetic <span className="text-muted-foreground">OS</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Agency System</div>
      </div>
    </div>
  );
}

function SidebarNav({ onNavigate, collapsed }) {
  const location = useLocation();
  const { canAccess, isMaster } = useModuleAccess();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [agentAlerts, setAgentAlerts] = useState(0);
  useEffect(() => {
    if (!isSuper) return;
    api.get("/agent-keys/alerts").then((r) => setAgentAlerts(r.data.filter((a) => !a.read).length)).catch(() => {});
  }, [isSuper, location.pathname]);
  const NAV = buildNav(isMaster, isSuper, agentAlerts);

  const groupsKey = user?.id ? `aos_sidebar_groups_${user.id}` : "aos_sidebar_groups";
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(groupsKey)) || {}; } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(groupsKey, JSON.stringify(openGroups)); } catch { /* ignore */ } }, [openGroups, groupsKey]);
  const toggleGroup = (label) => setOpenGroups((p) => ({ ...p, [label]: !(p[label] ?? true) }));

  const isChildActive = (child) => (child.end ? location.pathname === child.to : location.pathname.startsWith(child.to));
  const visibleChildren = (children) => children.filter((c) => !(c.moduleKey && !canAccess(c.moduleKey)));
  const childLabel = (child) => (child.to === "/hr" && !isMaster ? "La mia area" : child.label);

  return (
    <nav className={cn("flex-1 space-y-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}>
      {NAV.map((item) => {
        if (item.moduleKey && !canAccess(item.moduleKey)) return null;
        if (item.group) {
          const kids = visibleChildren(item.children);
          if (kids.length === 0) return null;
          const containsActive = kids.some(isChildActive);
          // Collapsed (solo icone): mostra le icone dei figli con tooltip, nessun toggle.
          if (collapsed) {
            return (
              <div key={item.label} className="border-t border-border/50 pt-2 mt-1 first:border-t-0 first:mt-0 first:pt-1" data-testid={item.testid}>
                {kids.map((child) => (
                  <NavLink key={child.to} to={child.to} data-testid={child.testid} onClick={onNavigate} title={childLabel(child)}
                    className={({ isActive }) => cn(linkClasses(isActive), "justify-center px-0")}>
                    <child.icon className="h-4 w-4" />
                  </NavLink>
                ))}
              </div>
            );
          }
          // La pagina attiva deve restare sempre visibile: se un figlio è attivo, il gruppo è mostrato aperto.
          const open = (openGroups[item.label] ?? true) || containsActive;
          return (
            <div key={item.label} className="pt-1" data-testid={item.testid}>
              <button
                type="button"
                onClick={() => toggleGroup(item.label)}
                data-testid={`${item.testid}-toggle`}
                aria-expanded={open}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors",
                  containsActive ? "text-foreground" : "text-muted-foreground/70 hover:text-foreground"
                )}
              >
                <item.icon className="h-4 w-4" />
                <span className="flex-1 text-left">{item.label}</span>
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", open ? "" : "-rotate-90")} />
              </button>
              {open && (
                <div className="mt-1 space-y-1 pl-3" data-testid={`${item.testid}-children`}>
                  {kids.map((child) => (
                    <NavLink key={child.to} to={child.to} end={child.end} data-testid={child.testid} onClick={onNavigate}
                      className={({ isActive }) => linkClasses(isActive)}>
                      <child.icon className="h-4 w-4" /> {childLabel(child)}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        }
        const active = item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={onNavigate}
            title={collapsed ? item.label : undefined}
            className={cn(linkClasses(active), collapsed && "justify-center px-0")}
          >
            <item.icon className="h-4 w-4" />
            {!collapsed && item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function SidebarFooter() {
  const [unread, setUnread] = useState(0);
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const pendingChanges = usePendingChanges(isSuper);
  useEffect(() => {
    let alive = true;
    const poll = () => import("@/lib/chatApi").then(({ chatApi }) =>
      chatApi.unreadCount().then((r) => alive && setUnread(r.unread || 0)).catch(() => {}));
    poll();
    const iv = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  return (
    <div className="flex gap-2 px-3 pb-4">
      {isSuper && (
        <NavLink to="/tommy" data-testid="nav-tommy"
          className={({ isActive }) => `relative flex flex-1 items-center justify-center gap-2 rounded-xl border p-2.5 transition-colors ${isActive ? "border-[#4FADC5] bg-[#4FADC5]/25" : "border-[#4FADC5]/50 bg-[#4FADC5]/15 hover:bg-[#4FADC5]/25"}`}>
          <Bot className="h-4 w-4 shrink-0 text-[#4FADC5]" />
          <span className="text-xs font-semibold text-[#3d8ba0] dark:text-[#7cc6d9]">Tommy</span>
          {pendingChanges > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#2f8aa3] px-1.5 text-[10px] font-semibold text-white" data-testid="nav-tommy-badge">{pendingChanges}</span>
          )}
        </NavLink>
      )}
      <NavLink to="/chat" data-testid="nav-chat"
        className={({ isActive }) => `relative flex flex-1 items-center justify-center gap-2 rounded-xl border p-2.5 transition-colors ${isActive ? "border-emerald-500 bg-emerald-500/25" : "border-emerald-500/50 bg-emerald-500/15 hover:bg-emerald-500/25"}`}>
        <MessageCircle className="h-4 w-4 shrink-0 text-emerald-500" />
        <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Chat</span>
        {unread > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[10px] font-semibold text-white" data-testid="nav-chat-badge">{unread}</span>
        )}
      </NavLink>
    </div>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("aos_sidebar_collapsed") === "1");
  useEffect(() => { localStorage.setItem("aos_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  return (
    <aside className={cn("sticky top-0 z-30 hidden h-screen shrink-0 flex-col border-r border-border/60 bg-card lg:flex transition-[width] duration-200", collapsed ? "w-16" : "w-60")} data-testid="app-sidebar">
      {collapsed ? (
        <div className="flex flex-col items-center gap-2 py-3">
          <img src="/logo-ae.png" alt="Aesthetic OS" className="h-7 w-auto max-w-[44px] object-contain" data-testid="sidebar-brand-logo-collapsed" />
          <button onClick={() => setCollapsed(false)} title="Espandi menu" data-testid="app-sidebar-toggle"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><PanelLeftOpen className="h-4 w-4" /></button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <SidebarBrand />
          <button onClick={() => setCollapsed(true)} title="Comprimi menu" data-testid="app-sidebar-toggle"
            className="mr-3 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><PanelLeftClose className="h-4 w-4" /></button>
        </div>
      )}
      <SidebarNav collapsed={collapsed} />
      {!collapsed && <SidebarFooter />}
    </aside>
  );
}

export function MobileSidebar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let startX = 0, startY = 0, tracking = false;
    const onStart = (e) => {
      if (window.innerWidth >= 1024) return;
      const t = e.touches[0];
      startX = t.clientX; startY = t.clientY;
      tracking = open || startX <= 24;
    };
    const onEnd = (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX, dy = t.clientY - startY;
      if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
      if (dx > 0 && !open && startX <= 24) setOpen(true);
      else if (dx < 0 && open) setOpen(false);
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="mobile-menu-trigger"
          aria-label="Apri menu di navigazione"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="flex w-72 flex-col gap-0 border-border/60 bg-card p-0" data-testid="mobile-sidebar-drawer">
        <SheetTitle className="sr-only">Navigazione</SheetTitle>
        <SidebarBrand />
        <SidebarNav onNavigate={() => setOpen(false)} />
        <SidebarFooter />
      </SheetContent>
    </Sheet>
  );
}
